import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { t } from 'i18next';
import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import { RadixButton, RadixCheckbox, RadixDialog, RadixInput, RadixPhoneField, RadixSpinner } from '@/components/radix';
import apiFetcher from '@/utils/interCeptor';
import { inviteStoreEmployeeV3Api } from '@/utils/Api/AuthV3';
import { uploadStoreFileApi } from '@/utils/Api/storeFileUpload';
import type { StoreEmployeeInviteReqBody } from '@/utils/Api/AuthV3/authV3.types';
import { CountryList } from '@/data/CountrylistTyped';
import { useMediaQuery } from '@/hooks/shared';
import CloseIcon from '@mui/icons-material/Close';
import { flattenStoreInviteForForm } from './inviteEmployeeApiShape';

export type EmployeeModalData = Record<string, unknown> | null;

export interface EmployeeModelProps {
    open: boolean;
    onClose: () => void;
    data: EmployeeModalData;
    handleRemove?: () => void;
    onSuccess?: () => void | Promise<void>;
}

export interface EmployeeFormValues {
    id: string | number | null;
    name: string;
    country_code: string;
    country_iso_code: string;
    phone_number: string;
    email: string;
    access_code: string;
    journal_access: boolean;
    image: string | File | null;
    role: string;
    remove_image: boolean;
    add_all_services: boolean;
    add_all_special_services: boolean;
}

function defaultIsoFromDialCode(dial: string): string {
    const trimmed = dial?.trim();
    if (!trimmed) return 'DK';
    const normalized = trimmed.startsWith('+') ? trimmed : `+${trimmed.replace(/^\+?/, '')}`;
    const found = Object.entries(CountryList).find(([, v]) => v.code === normalized);
    return found?.[0] ?? 'DK';
}

function phoneDigitsForIso(iso: string, rawDigits: string): string {
    const meta = CountryList[iso as keyof typeof CountryList] ?? CountryList.DK;
    return rawDigits.replace(/\D/g, '').slice(0, meta.phoneLength);
}

function isInvitePendingEmployee(data: EmployeeModalData): boolean {
    if (!data || typeof data !== 'object') return false;
    return Boolean((data as { invite_pending?: boolean }).invite_pending);
}

function resolveEmployeeImageDisplaySrc(image: string | File | null, imgPreview: string | null): string | null {
    if (image instanceof File) return imgPreview;
    if (typeof image !== 'string' || !image.trim()) return null;
    const trimmed = image.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    return `${process.env.REACT_APP_IMG_URL}${trimmed}`;
}

/** Build `req_body` JSON for v1 employee PATCH (excludes id, image file, UI-only country_iso_code). */
function buildEmployeeV1ReqBody(values: EmployeeFormValues): Record<string, unknown> {
    const { country_iso_code: _iso, id: _id, image: _image, ...rest } = values;
    void _iso;
    void _id;
    void _image;
    return rest as Record<string, unknown>;
}

function buildInitialValues(data: EmployeeModalData): EmployeeFormValues {
    if (!data) {
        return {
            id: null,
            name: '',
            country_code: '+45',
            country_iso_code: 'DK',
            phone_number: '',
            email: '',
            access_code: '',
            journal_access: false,
            image: '',
            role: 'EMPLOYEE',
            remove_image: false,
            add_all_services: false,
            add_all_special_services: false,
        };
    }
    const src =
        flattenStoreInviteForForm(data as Record<string, unknown>) ?? (data as Record<string, unknown>);
    const phoneRaw = String(src.phone_number ?? (src as { phone?: string }).phone ?? '').replace(/\D/g, '');
    const countryCode = String(src.country_code ?? '+45');
    const candidateIso =
        String(
            (src as { country_iso_code?: string }).country_iso_code ??
                (src as { countryISOCode?: string }).countryISOCode ??
                '',
        ) || defaultIsoFromDialCode(countryCode);
    const countryIso = CountryList[candidateIso as keyof typeof CountryList]
        ? candidateIso
        : defaultIsoFromDialCode(countryCode);
    const countryMeta = CountryList[countryIso as keyof typeof CountryList];
    const maxDigits = countryMeta?.phoneLength ?? 8;
    const rawId = (src as { id?: string | number | null }).id;
    const idVal =
        rawId === null || rawId === undefined || rawId === ''
            ? null
            : typeof rawId === 'string' || typeof rawId === 'number'
              ? rawId
              : null;

    return {
        id: idVal,
        name: String(src.name ?? ''),
        country_code: countryCode,
        country_iso_code: countryIso,
        phone_number: phoneRaw.slice(0, maxDigits),
        email: String(src.email ?? ''),
        access_code: String(src.access_code ?? ''),
        journal_access: Boolean(src.journal_access),
        image: (src.image as string | File | null) ?? '',
        role: String(src.role ?? 'EMPLOYEE'),
        remove_image: false,
        add_all_services: Boolean(src.add_all_services),
        add_all_special_services: Boolean(src.add_all_special_services),
    };
}

export default function EmployeeModel({ open, onClose, data, handleRemove, onSuccess }: EmployeeModelProps) {
    const user = useSelector((state: { user?: { data?: Record<string, unknown> } }) => state.user?.data);
    const [imgPreview, setImgPreview] = useState<string | null>(null);
    const isDesktop = useMediaQuery('(min-width: 960px)');

    const isInviteDetailsView = isInvitePendingEmployee(data);
    const isEdit = Boolean(data?.id);

    const validationSchema = useMemo(
        () =>
            isInviteDetailsView
                ? Yup.object({})
                : isEdit
                  ? Yup.object({
                      name: Yup.string().required(t('Setting.PleaseEnterName')),
                      email: Yup.string().email(t('Setting.InvalidEmailFormat')).required(t('Setting.EmailIsRequired')),
                      phone_number: Yup.string()
                          .nullable()
                          .test('phone-len', function (v) {
                              if (!v || v.length === 0) return true;
                              const parent = this.parent as EmployeeFormValues;
                              const iso = (parent.country_iso_code || 'DK') as keyof typeof CountryList;
                              const expected = (CountryList[iso] ?? CountryList.DK).phoneLength;
                              if (v.length === expected) return true;
                              return this.createError({
                                  message: t('Setting.PhoneMustMatchCountryLength', { count: expected }),
                              });
                          }),
                      access_code: Yup.string()
                          .nullable()
                          .test('ac-len', t('Setting.MustBeExactly6Digits'), (v) => {
                              if (!v || v.length === 0) return true;
                              return v.length === 6;
                          }),
                  })
                : Yup.object({
                      name: Yup.string().required(t('Setting.PleaseEnterName')),
                      email: Yup.string().email(t('Setting.InvalidEmailFormat')).required(t('Setting.EmailIsRequired')),
                      phone_number: Yup.string()
                          .nullable()
                          .test('phone-len', function (v) {
                              if (!v || v.length === 0) return true;
                              const parent = this.parent as EmployeeFormValues;
                              const iso = (parent.country_iso_code || 'DK') as keyof typeof CountryList;
                              const expected = (CountryList[iso] ?? CountryList.DK).phoneLength;
                              if (v.length === expected) return true;
                              return this.createError({
                                  message: t('Setting.PhoneMustMatchCountryLength', { count: expected }),
                              });
                          }),
                  }),
        [isEdit, isInviteDetailsView],
    );

    const initialValues = useMemo(() => buildInitialValues(data), [data]);

    const formik = useFormik<EmployeeFormValues>({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            if (isInviteDetailsView) return;
            if (isEdit) {
                const employeeId = values.id;
                if (!employeeId) {
                    toast.error(t('Setting.FailedToUpdateEmployee'));
                    return;
                }
                try {
                    const formData = new FormData();
                    formData.append('req_body', JSON.stringify(buildEmployeeV1ReqBody(values)));
                    if (values.image != null && typeof values.image === 'object') {
                        formData.append('image', values.image);
                    }
                    await apiFetcher.patch(`/api/v1/store/employee/${employeeId}`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    toast.success(t('Setting.EmployeeUpdated'));
                    await onSuccess?.();
                    onClose();
                } catch (error: any) {
                    toast.error(error?.detail ?? error?.msg ?? t('Setting.FailedToUpdateEmployee'));
                }
                return;
            }

            try {
                const iso = (values.country_iso_code || 'DK') as keyof typeof CountryList;
                const expectedPhoneLen = (CountryList[iso] ?? CountryList.DK).phoneLength;
                const body: StoreEmployeeInviteReqBody = {
                    name: values.name.trim(),
                    email: values.email.trim(),
                    country_code: values.country_code || '+45',
                    role: values.role || 'EMPLOYEE',
                    journal_access: values.journal_access,
                    add_all_services: values.add_all_services,
                    add_all_special_services: values.add_all_special_services,
                };
                if (values.phone_number && values.phone_number.length === expectedPhoneLen) {
                    body.phone_number = values.phone_number;
                }

                if (values.image instanceof File) {
                    try {
                        const fileUrl = await uploadStoreFileApi(values.image);
                        body.image = fileUrl;
                    } catch {
                        toast.error(t('Setting.EmployeeImageUploadFailed'));
                        return;
                    }
                }

                await inviteStoreEmployeeV3Api(body);
                toast.success(t('Setting.EmployeeInviteSent'));
                await onSuccess?.();
                onClose();
                formik.resetForm({ values: buildInitialValues(null) });
                setImgPreview(null);
            } catch (error: any) {
                toast.error(error?.detail ?? error?.msg ?? t('Setting.EmployeeInviteFailed'));
            }
        },
    });

    const fieldsDisabled = formik.isSubmitting || isInviteDetailsView;

    useEffect(() => {
        if (!open) return;
        setImgPreview(null);
    }, [open, data]);

    const imageSrc = resolveEmployeeImageDisplaySrc(formik.values.image, imgPreview);

    const canDelete =
        isEdit && !isInviteDetailsView && user?.id !== data?.id && data?.role !== 'ADMIN' && handleRemove;

    return (
        <RadixDialog
            open={open}
            position={isDesktop ? 'center' : 'bottom'}
            onOpenChange={(next) => {
                if (!next) onClose();
            }}
            title={
                isInviteDetailsView
                    ? t('Setting.InviteEmployeeDetails')
                    : isEdit
                      ? t('Setting.EditEmployee')
                      : t('Setting.AddEmployee')
            }
            footer={
                isInviteDetailsView ? (
                    <div className="flex w-full flex-row justify-end">
                        <RadixButton type="button" variant="outline" size={isDesktop ? 'base' : 'sm'} onClick={onClose}>
                            {t('Setting.Cancel')}
                        </RadixButton>
                    </div>
                ) : (
                    <div className="flex w-full gap-2 flex-row justify-end">
                        {canDelete ? (
                            <RadixButton
                                type="button"
                                variant="danger"
                                size={isDesktop ? 'base' : 'sm'}
                                className="w-fit"
                                onClick={handleRemove}
                                disabled={formik.isSubmitting}
                            >
                                {t('Setting.DeleteEmployee')}
                            </RadixButton>
                        ) : null}
                        <RadixButton
                            type="submit"
                            form="employee-modal-form"
                            variant="primary"
                            className="w-fit"
                            size={isDesktop ? 'base' : 'sm'}
                            disabled={formik.isSubmitting}
                        >
                            {formik.isSubmitting ? (
                                <RadixSpinner variant="white" size="sm" />
                            ) : isEdit ? (
                                t('Setting.SaveChanges')
                            ) : (
                                t('Setting.SendEmployeeInvitation')
                            )}
                        </RadixButton>
                    </div>
                )
            }
        >
            <form
                id="employee-modal-form"
                className="flex flex-col gap-4 px-1"
                onSubmit={(e) => {
                    if (isInviteDetailsView) {
                        e.preventDefault();
                        return;
                    }
                    void formik.handleSubmit(e);
                }}
                noValidate
            >
                <RadixInput
                    id="employee-name"
                    name="name"
                    label={t('Setting.EmployeeName')}
                    placeholder={t('Setting.EmployeeName')}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && formik.errors.name ? String(formik.errors.name) : undefined}
                    disabled={fieldsDisabled}
                    className="!rounded-xl !bg-white !h-11 !border-[#E6E1D9]"
                />

                <RadixPhoneField
                    id="employee-phone"
                    name="phone_number"
                    label={t('Setting.MobileNumber(optional)')}
                    placeholder={t('Common.MobileNumber')}
                    value={{
                        country_code: formik.values.country_code,
                        phone: formik.values.phone_number,
                        countryISOCode: formik.values.country_iso_code,
                    }}
                    onChange={(pv) => {
                        const digits = phoneDigitsForIso(pv.countryISOCode, pv.phone);
                        void formik.setFieldValue('country_code', pv.country_code);
                        void formik.setFieldValue('country_iso_code', pv.countryISOCode);
                        void formik.setFieldValue('phone_number', digits);
                    }}
                    onBlur={() => void formik.setFieldTouched('phone_number', true)}
                    error={
                        formik.touched.phone_number && formik.errors.phone_number
                            ? String(formik.errors.phone_number)
                            : undefined
                    }
                    disabled={fieldsDisabled}
                    className="!rounded-r-xl !border-[#E6E1D9] !bg-white"
                />

                <RadixInput
                    id="employee-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    label={t('Common.Email')}
                    placeholder={t('Common.Email')}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && formik.errors.email ? String(formik.errors.email) : undefined}
                    disabled={fieldsDisabled}
                    className="!rounded-xl !bg-white !h-11 !border-[#E6E1D9]"
                />

                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
                    <RadixCheckbox
                        checked={formik.values.add_all_services}
                        onChange={(checked) => void formik.setFieldValue('add_all_services', checked)}
                        disabled={fieldsDisabled}
                        label={t('Setting.allservice')}
                    />
                    <RadixCheckbox
                        checked={formik.values.add_all_special_services}
                        onChange={(checked) => void formik.setFieldValue('add_all_special_services', checked)}
                        disabled={fieldsDisabled}
                        label={t('Setting.allSpecialoffer')}
                    />
                </div>

                <div className="space-y-2">
                    <p className="text-sm font-medium text-text-secondary">{t('Setting.EmployeeImage(optional)')}</p>
                    <div className="flex flex-wrap items-center gap-3">
                        <input
                            type="file"
                            accept="image/*"
                            id="employee-image-upload"
                            className="sr-only"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                void formik.setFieldValue('image', file);
                                void formik.setFieldValue('remove_image', false);
                                const reader = new FileReader();
                                reader.onloadend = () => setImgPreview(reader.result as string);
                                reader.readAsDataURL(file);
                            }}
                            disabled={fieldsDisabled}
                        />
                        <label
                            htmlFor="employee-image-upload"
                            className={`inline-flex items-center justify-center rounded-xl border border-dashed border-[#E6E1D9] bg-[#F9F8F6] px-4 py-2 text-sm font-medium text-gray-700 ${fieldsDisabled ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:bg-[#F0EDE8]'}`}
                        >
                            {t('Common.Upload')}
                        </label>
                        {imageSrc ? (
                            <div className="relative h-48 w-48">
                                <img src={imageSrc} alt="" className="w-full h-full rounded-lg object-cover" />

                                {!isInviteDetailsView &&
                                    (formik.values.image instanceof File ||
                                        (typeof formik.values.image === 'string' && formik.values.image)) && (
                                        <RadixButton
                                            className="absolute top-2 right-2 p-0 !rounded-full bg-white"
                                            iconOnly
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                void formik.setFieldValue('image', '');
                                                void formik.setFieldValue('remove_image', true);
                                                setImgPreview(null);
                                            }}
                                            disabled={fieldsDisabled}
                                            children={<CloseIcon />}
                                        />
                                    )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </form>
        </RadixDialog>
    );
}
